import { Card, Checkbox, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import MDAuditModal from "components/MDAuditModal";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { trueOrFalseEnum } from "utils/Enums";
import { verifyVariable, infoAviso, successAlert, errorAlert, warningAlert } from "utils/Functions";
import { post, postNoResponse, get } from "utils/WebService";
import TeamsCreateEdit from "./TeamsCreateEdit";
import ReplicarClientesDialog from "./ReplicarClientesDialog";
import { ESCURO, coresDoTema } from "assets/coresTema";

export default function TeamsConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [auditTitle, setAuditTitle] = useState("");
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAudit, setLoadingAudit] = useState(false);
    // Replicar os clientes liberados de uma pessoa da equipe para as outras: { id, nome } da equipe
    const [replicarClientes, setReplicarClientes] = useState(null);

    const handleOpenAuditoriaRow = (row) => {
        const id = row.id || row.cvId;
        setAuditTitle(`Auditoria da Equipe: ${row.nome || ("ID " + id)}`);
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get(`/equipe/${id}/historico`)
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria da equipe:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const handleOpenAuditoriaGeral = () => {
        setAuditTitle("Histórico de Auditoria - Todas as Equipes");
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get("/equipe/historico-geral")
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria geral de equipes:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const [filters, setFilters] = useState({
        nome: '',
        ativo: null,
        usuarioId: null
    });

    // Filtro "Usuário": as equipes em que ele está (operador, coordenador, supervisor ou backoffice) e, no resumo, o papel
    // em cada uma e as carteiras que ele enxerga pela regra de visibilidade (GET /equipe/do-usuario)
    const [usuarios, setUsuarios] = useState([]);
    const [resumoUsuario, setResumoUsuario] = useState(null);

    useEffect(() => {
        fetchData({ ...tableState });
        get("/users/list-all-including-inactive")
            .then((res) => {
                const lista = res?.list || res?.data || (Array.isArray(res) ? res : []);
                setUsuarios([...lista].sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")));
            })
            .catch(() => setUsuarios([]));
    }, []);

    const carregarResumoUsuario = (usuarioId) => {
        if (!usuarioId) {
            setResumoUsuario(null);
            return;
        }
        get(`/equipe/do-usuario/${encodeURIComponent(usuarioId)}`)
            .then((res) => setResumoUsuario(res))
            .catch(() => setResumoUsuario(null));
    };

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };

        tableState.current = updatedTableState;

        post("/equipe/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });

    }, []);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
    }

    const handleDelete = (id) => {
        infoAviso(
            "Deseja realmente excluir essa equipe?",
            async () => {
                await removeEquipe(id);
            },
            () => null
        );
    };

    const removeEquipe = async (id) => {
        if (id) {
            postNoResponse(`/equipe/delete/${id}`)
                .then(() => {
                    successAlert("Equipe removida com sucesso!", () => null);
                    fetchData({ ...tableState });
                })
                .catch(e => {
                    console.error(e);
                    warningAlert("Registro em uso, exclusão não permitida");
                });
        }
    };

    const userColumns = [
        {
            header: "nome",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Ativo",
            accessorKey: "ativo",
            cell: info => (
                <Checkbox
                    checked={info.getValue()}
                    disabled
                    color="primary"
                    inputProps={{ 'aria-label': 'Ativo' }}
                />
            ),
            isSorted: true
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Replicar clientes liberados entre as pessoas da equipe">
                        <IconButton
                            onClick={() => setReplicarClientes({ id: info.row.original.id, nome: info.row.original.nome })}>
                            <MDTypography variant="h4">
                                <Icon color="info" sx={{ marginRight: 1 }}>group_add</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Histórico de Auditoria">
                        <IconButton
                            onClick={() => handleOpenAuditoriaRow(info.row.original)}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>history</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                        <IconButton
                            onClick={() => handleDelete(info.row.original.id)}>
                            <MDTypography variant="h4">
                                <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </>
            ),
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        let filtered = [];

        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey])) {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        })
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
        carregarResumoUsuario(filters.usuarioId);
    };

    const handleCleanFilter = () => {
        setFilters({
            nome: '',
            ativo: null,
            usuarioId: null
        })
        setResumoUsuario(null);
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [] });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <TeamsCreateEdit
                        id={selectedId}
                        closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="Informe o nome..."
                                value={filters.nome}
                                onChange={({ target }) => setFilters({ ...filters, nome: target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilter();
                                    }
                                }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Ativo</InputLabel>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.name}
                                name="ativo"
                                placeholder="Ambos"
                                value={trueOrFalseEnum.find(({ value }) => value == filters.ativo) || null}
                                options={trueOrFalseEnum}
                                onChange={(selected) => {
                                    setFilters({ ...filters, ativo: selected?.value })
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Usuário</InputLabel>
                            <MDSelect
                                isMulti={false}
                                isClearable
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.nome}
                                name="usuarioId"
                                placeholder="Todos"
                                value={usuarios.find((u) => u.id === filters.usuarioId) || null}
                                options={usuarios}
                                onChange={(selected) => setFilters({ ...filters, usuarioId: selected?.id || null })}
                            />
                            <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
                                Lista as equipes em que ele está, em qualquer papel.
                            </MDTypography>
                        </Grid>

                        <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                            <MDBox display="flex" gap={1}>
                                <MDButton
                                    size="medium"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={() => {
                                        setSelectedId(null);
                                        setIsCreateEdit(true);
                                    }}>
                                    Adicionar Equipe
                                </MDButton>
                                <MDButton
                                    size="medium"
                                    variant="outlined"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleOpenAuditoriaGeral}>
                                    <Icon sx={{ mr: 0.5 }}>history</Icon> Auditoria Geral
                                </MDButton>
                            </MDBox>
                            <MDBox>
                                <MDButton
                                    size="medium"
                                    sx={{ marginRight: 2 }}
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleCleanFilter}>
                                    Limpar filtro
                                </MDButton>
                                <MDButton
                                    size="medium"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleApplyFilter}>
                                    Aplicar filtro
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                {resumoUsuario && (() => {
                    const cores = coresDoTema(darkMode);
                    const equipes = resumoUsuario.equipes || [];
                    return (
                        <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: cores.cartao, borderColor: cores.divisoria }}>
                            <MDTypography variant="button" fontWeight="bold" color="text" display="block" mb={0.5}>
                                {resumoUsuario.usuario || "Usuário"}: {equipes.length === 0 ? "não está em nenhuma equipe" : `${equipes.length} equipe(s)`}
                            </MDTypography>
                            {equipes.map((e, i) => (
                                <MDTypography key={i} variant="caption" color="text" display="block">
                                    • <b>{e.nome}</b>{e.ativa ? "" : " (inativa: não conta para a visibilidade)"}: {(e.papeis || []).join(", ") || "sem papel"}
                                    {" · carteiras: "}{(e.carteiras || []).length ? e.carteiras.join(", ") : "nenhuma"}
                                </MDTypography>
                            ))}
                            <MDTypography variant="caption" color="text" display="block" mt={0.75}>
                                {resumoUsuario.veTodasAsCarteiras
                                    ? "Não está em equipe ativa: enxerga todas as carteiras (Base de Dados, relatórios...)."
                                    : (resumoUsuario.carteirasVisiveis || []).length
                                        ? <>Enxerga só as carteiras: <b>{resumoUsuario.carteirasVisiveis.join(", ")}</b> (a regra vale para qualquer perfil, inclusive Administrador).</>
                                        : "Está em equipe ativa sem carteira: não enxerga nenhuma ficha."}
                            </MDTypography>
                        </Card>
                    );
                })()}
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    changeFilter={aplicar}
                    tableState={tableState} />
            </Grid>

            {replicarClientes && (
                <ReplicarClientesDialog equipe={replicarClientes} onClose={() => setReplicarClientes(null)} />
            )}
            <MDAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                title={auditTitle}
                loading={loadingAudit}
                logs={auditLogs}
                entityLabel="Equipe"
            />
        </Grid >
    );
}

